import { Add, Delete, Edit, Home, HomeOutlined, Star, StarBorder } from '@mui/icons-material';
import { Button, IconButton, Tooltip, Typography } from '@mui/material';
import { DashboardBriefDto } from '@thingslog/repositories';
import { cn } from '@thingslog/ui-components';
import React, { FC, KeyboardEvent, MouseEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type DashboardSelectionListVariant = 'sidebar' | 'page';

interface Props {
  dashboards: DashboardBriefDto[];
  currentDashboardId?: string;
  variant?: DashboardSelectionListVariant;
  onSelectDashboard: (dashboardId: string) => void;
  onSetDefaultDashboard: (dashboardId: string) => void;
  onToggleFavoriteDashboard: (dashboardId: string, isFavorite: boolean) => void;
  onEditDashboard?: (dashboardId: string) => void;
  onDeleteDashboard?: (dashboardId: string) => void;
  onAddDashboard?: () => void;
  onAddDefaultDashboard?: () => void;
  isAddDefaultDashboardDisabled?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}

interface DashboardSelectionListItemProps {
  dashboard: DashboardBriefDto;
  isCurrent: boolean;
  isDefault: boolean;
  isFavorite: boolean;
  variant: DashboardSelectionListVariant;
  onSelectDashboard: (dashboardId: string) => void;
  onSetDefaultDashboard: (dashboardId: string) => void;
  onToggleFavoriteDashboard: (dashboardId: string, isFavorite: boolean) => void;
  onEditDashboard?: (dashboardId: string) => void;
  onDeleteDashboard?: (dashboardId: string) => void;
}

const sortDashboards = (dashboards: DashboardBriefDto[]): DashboardBriefDto[] => {
  return [...dashboards].sort((first: DashboardBriefDto, second: DashboardBriefDto): number => {
    if (first.isDefault && !second.isDefault) return -1;
    if (!first.isDefault && second.isDefault) return 1;

    if (first.isFavorite && !second.isFavorite) return -1;
    if (!first.isFavorite && second.isFavorite) return 1;

    return first.name.localeCompare(second.name);
  });
};

const handleItemKeyDown = (
  event: KeyboardEvent<HTMLDivElement>,
  dashboardId: string,
  onSelectDashboard: (dashboardId: string) => void
): void => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onSelectDashboard(dashboardId);
  }
};

const handleActionClick = (event: MouseEvent<HTMLButtonElement>, callback: () => void): void => {
  event.stopPropagation();
  callback();
};

const DashboardSelectionListItem: FC<DashboardSelectionListItemProps> = ({
  dashboard,
  isCurrent,
  isDefault,
  isFavorite,
  variant,
  onSelectDashboard,
  onSetDefaultDashboard,
  onToggleFavoriteDashboard,
  onEditDashboard,
  onDeleteDashboard,
}: DashboardSelectionListItemProps) => {
  const { t } = useTranslation();
  const [rowElement, setRowElement] = useState<HTMLDivElement | null>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);
  const nameRef = useRef<HTMLSpanElement | null>(null);

  const checkNameTruncation = (): void => {
    const nameElement = nameRef.current;

    if (!nameElement) {
      setIsNameTruncated(false);
      return;
    }

    setIsNameTruncated(nameElement.scrollWidth > nameElement.clientWidth);
  };

  return (
    <div
      ref={setRowElement}
      role="button"
      tabIndex={0}
      className={cn(
        'group flex min-w-0 cursor-pointer items-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-cyan-500',
        variant === 'sidebar' && 'gap-2 rounded-md px-2 py-1.5 hover:bg-gray-100 focus:bg-gray-100',
        variant === 'page' &&
          'gap-3 rounded-md border border-solid border-gray-200 bg-white px-3 py-2 hover:border-cyan-200 hover:bg-cyan-50/40 focus:border-cyan-300 focus:bg-cyan-50/50',
        isCurrent &&
          (variant === 'sidebar'
            ? 'bg-cyan-50 text-cyan-900 hover:bg-cyan-50 focus:bg-cyan-50'
            : 'border-cyan-300 bg-cyan-50 text-cyan-900')
      )}
      data-dashboard-tour={variant === 'sidebar' ? 'dashboard-selection-row' : undefined}
      onClick={(): void => onSelectDashboard(dashboard.id)}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>): void =>
        handleItemKeyDown(event, dashboard.id, onSelectDashboard)
      }
    >
      <div className="min-w-0 flex-1 text-left">
        <Tooltip
          placement="right"
          PopperProps={{ anchorEl: rowElement }}
          title={isNameTruncated ? dashboard.name : ''}
        >
          <Typography
            ref={nameRef}
            variant="body2"
            component="span"
            className="block truncate text-left"
            fontWeight={isCurrent ? 600 : 400}
            onMouseEnter={checkNameTruncation}
            onFocus={checkNameTruncation}
          >
            {dashboard.name}
          </Typography>
        </Tooltip>
      </div>
      <div
        className={cn(
          'flex shrink-0 items-center gap-1',
          variant === 'page' && 'border-0 border-l border-solid border-gray-200 pl-3'
        )}
      >
        <Tooltip
          placement="right"
          PopperProps={{ anchorEl: rowElement }}
          title={t(
            'dashboard_favorite_tooltip',
            'Favorite dashboards are pinned near the top of this list for quick access.'
          )}
        >
          <IconButton
            size="small"
            color={isFavorite ? 'warning' : 'default'}
            aria-label={
              isFavorite
                ? t('dashboard_remove_from_favorites', 'Remove from favorites')
                : t('dashboard_add_to_favorites', 'Add to favorites')
            }
            data-dashboard-tour={variant === 'sidebar' ? 'dashboard-selection-favorite' : undefined}
            onClick={(event: MouseEvent<HTMLButtonElement>): void =>
              handleActionClick(event, (): void =>
                onToggleFavoriteDashboard(dashboard.id, !isFavorite)
              )
            }
          >
            {isFavorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip
          placement="right"
          PopperProps={{ anchorEl: rowElement }}
          title={t(
            'dashboard_default_tooltip',
            'The default dashboard stays at the very top and is used as your primary dashboard.'
          )}
        >
          <IconButton
            size="small"
            color={isDefault ? 'primary' : 'default'}
            aria-label={
              isDefault
                ? t('dashboard_default_dashboard', 'Default dashboard')
                : t('dashboard_set_as_default', 'Set as default')
            }
            data-dashboard-tour={variant === 'sidebar' ? 'dashboard-selection-default' : undefined}
            onClick={(event: MouseEvent<HTMLButtonElement>): void =>
              handleActionClick(event, (): void => onSetDefaultDashboard(dashboard.id))
            }
          >
            {isDefault ? <Home fontSize="small" /> : <HomeOutlined fontSize="small" />}
          </IconButton>
        </Tooltip>
        {onEditDashboard &&
          (variant === 'page' ? (
            <Button
              size="small"
              variant="text"
              startIcon={<Edit fontSize="small" />}
              onClick={(event: MouseEvent<HTMLButtonElement>): void =>
                handleActionClick(event, (): void => onEditDashboard(dashboard.id))
              }
            >
              {t('edit', 'Edit')}
            </Button>
          ) : (
            <Tooltip title={t('dashboard_edit_dashboard', 'Edit dashboard')}>
              <IconButton
                size="small"
                aria-label={t('dashboard_edit_dashboard', 'Edit dashboard')}
                onClick={(event: MouseEvent<HTMLButtonElement>): void =>
                  handleActionClick(event, (): void => onEditDashboard(dashboard.id))
                }
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
        {onDeleteDashboard &&
          (variant === 'page' ? (
            <Button
              size="small"
              variant="text"
              color="error"
              startIcon={<Delete fontSize="small" />}
              onClick={(event: MouseEvent<HTMLButtonElement>): void =>
                handleActionClick(event, (): void => onDeleteDashboard(dashboard.id))
              }
            >
              {t('delete', 'Delete')}
            </Button>
          ) : (
            <Tooltip title={t('dashboard_delete_dashboard', 'Delete dashboard')}>
              <IconButton
                size="small"
                color="error"
                aria-label={t('dashboard_delete_dashboard', 'Delete dashboard')}
                onClick={(event: MouseEvent<HTMLButtonElement>): void =>
                  handleActionClick(event, (): void => onDeleteDashboard(dashboard.id))
                }
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
      </div>
    </div>
  );
};

export const DashboardSelectionList: FC<Props> = ({
  dashboards,
  currentDashboardId,
  variant = 'sidebar',
  onSelectDashboard,
  onSetDefaultDashboard,
  onToggleFavoriteDashboard,
  onEditDashboard,
  onDeleteDashboard,
  onAddDashboard,
  onAddDefaultDashboard,
  isAddDefaultDashboardDisabled = false,
  emptyTitle,
  emptyDescription,
}: Props) => {
  const { t } = useTranslation();
  const sortedDashboards = useMemo(() => sortDashboards(dashboards), [dashboards]);
  const resolvedEmptyTitle =
    emptyTitle ?? t('dashboards_empty_no_results_title', 'No dashboards found');

  if (sortedDashboards.length === 0) {
    return (
      <div
        className={cn(
          'flex min-w-0 flex-col items-center gap-3 px-3 py-8 text-center',
          variant === 'page' && 'rounded-md border border-dashed border-gray-300 bg-gray-50'
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
          <HomeOutlined fontSize="small" />
        </div>
        <div className="min-w-0">
          <Typography variant="subtitle1" component="p" fontWeight={600}>
            {resolvedEmptyTitle}
          </Typography>
          {variant === 'page' && emptyDescription && (
            <Typography variant="body2" className="mt-1 text-gray-600">
              {emptyDescription}
            </Typography>
          )}
        </div>
        {variant === 'page' && (onAddDashboard || onAddDefaultDashboard) && (
          <div className="flex flex-col gap-2 sm:flex-row">
            {onAddDashboard && (
              <Button variant="outlined" startIcon={<Add />} onClick={onAddDashboard}>
                {t('dashboard_add_dashboard', 'Add Dashboard')}
              </Button>
            )}
            {onAddDefaultDashboard && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Add />}
                disabled={isAddDefaultDashboardDisabled}
                onClick={onAddDefaultDashboard}
              >
                {t('dashboard_add_default_dashboard', 'Add Default Dashboard')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0 flex-col', variant === 'page' ? 'gap-2' : 'gap-1')}>
      {sortedDashboards.map((dashboard: DashboardBriefDto): JSX.Element => {
        const isCurrent = dashboard.id === currentDashboardId;
        const isDefault = Boolean(dashboard.isDefault);
        const isFavorite = Boolean(dashboard.isFavorite);

        return (
          <DashboardSelectionListItem
            key={dashboard.id}
            dashboard={dashboard}
            isCurrent={isCurrent}
            isDefault={isDefault}
            isFavorite={isFavorite}
            variant={variant}
            onSelectDashboard={onSelectDashboard}
            onSetDefaultDashboard={onSetDefaultDashboard}
            onToggleFavoriteDashboard={onToggleFavoriteDashboard}
            onEditDashboard={onEditDashboard}
            onDeleteDashboard={onDeleteDashboard}
          />
        );
      })}
    </div>
  );
};
